/**
 * Surface property tables — the exact values from the original engine.
 *
 * The single most important detail is `calculateFriction`: friction is
 * speed-dependent, so fast balls glide and slow balls grip and settle. This is
 * what gives the original its signature feel.
 */
import { Surface } from '../types.js';
import { DIAG, DOWNHILL_ACCEL } from '../constants.js';

/** Base per-frame velocity multiplier for a surface (lower = more drag). */
export function baseFriction(s: Surface): number {
  switch (s) {
    case Surface.Dirt:
      return 0.92;
    case Surface.Mud:
      return 0.8;
    case Surface.Ice:
      return 0.999;
    case Surface.TeleBlueIn:
    case Surface.TeleRedIn:
    case Surface.TeleYellowIn:
    case Surface.TeleGreenIn:
      return 0.9975;
    case Surface.Water:
    case Surface.Acid:
      return 0.0;
    case Surface.WaterSwamp:
    case Surface.AcidSwamp:
      return 0.95;
    case Surface.OneWayN:
    case Surface.OneWayE:
    case Surface.OneWayS:
    case Surface.OneWayW:
      return 0.995;
    case Surface.Hole:
      return 0.96;
    case Surface.Mine:
    case Surface.BigMine:
      return 1.0;
    case Surface.BlownMine:
    case Surface.BlownBigMine:
    case Surface.MagnetAttract:
      return 0.9;
    case Surface.Grass:
    case Surface.SpeedN:
    case Surface.SpeedNE:
    case Surface.SpeedE:
    case Surface.SpeedSE:
    case Surface.SpeedS:
    case Surface.SpeedSW:
    case Surface.SpeedW:
    case Surface.SpeedNW:
    case Surface.FakeBlock:
    case Surface.SunkMoveable:
      return 0.9935;
    default:
      return 0.9935;
  }
}

/**
 * Speed-dependent friction multiplier (authentic):
 *   eff = base + (1 - base) * (0.75 * speed / 6.5)
 */
export function calculateFriction(s: Surface, speed: number): number {
  const base = baseFriction(s);
  const speedFactor = (0.75 * speed) / 6.5;
  return base + (1 - base) * speedFactor;
}

/** Is this surface a wall the ball bounces off? */
export function isSolid(s: Surface): boolean {
  return (
    (s >= Surface.Block && s <= Surface.OneWayW && s !== Surface.FakeBlock) ||
    s === Surface.MoveableBlock ||
    s === Surface.MoveableBlock2 ||
    (s >= Surface.Breakable4 && s <= Surface.Breakable1)
  );
}

/**
 * Directional solidity (authentic one-way walls): a one-way tile lets the
 * ball pass along its arrow (and sideways) but is a wall against motion
 * opposing the arrow. All other surfaces defer to `isSolid`.
 */
export function isSolidFor(s: Surface, vx: number, vy: number): boolean {
  switch (s) {
    case Surface.OneWayN:
      return vy > 0;
    case Surface.OneWayS:
      return vy < 0;
    case Surface.OneWayE:
      return vx < 0;
    case Surface.OneWayW:
      return vx > 0;
    default:
      return isSolid(s);
  }
}

export function isBreakable(s: Surface): boolean {
  return s >= Surface.Breakable4 && s <= Surface.Breakable1;
}

/** Next stage of a cracking breakable wall, or null when it shatters. */
export function breakableNext(s: Surface): Surface | null {
  switch (s) {
    case Surface.Breakable4:
      return Surface.Breakable3;
    case Surface.Breakable3:
      return Surface.Breakable2;
    case Surface.Breakable2:
      return Surface.Breakable1;
    case Surface.Breakable1:
      return null; // shattered — tile reverts to its background
    default:
      return s;
  }
}

export function isMoveable(s: Surface): boolean {
  return s === Surface.MoveableBlock || s === Surface.MoveableBlock2;
}

export function isMagnet(s: Surface): boolean {
  return s === Surface.MagnetAttract || s === Surface.MagnetRepel;
}

export function isWater(s: Surface): boolean {
  return s === Surface.Water || s === Surface.Acid || s === Surface.WaterSwamp || s === Surface.AcidSwamp;
}

export function isDeadlyWater(s: Surface): boolean {
  return s === Surface.Water || s === Surface.Acid;
}

/**
 * Reflection energy factor for a wall surface (authentic `getSpeedEffect`).
 * Bouncy blocks restore energy toward max speed; sticky blocks nearly kill it.
 * Returns a multiplier applied to the reflected velocity components.
 */
export function wallBounciness(s: Surface, speed: number, bounciness: number): { factor: number; nextBounciness: number } {
  switch (s) {
    case Surface.Block:
      return { factor: 0.81, nextBounciness: bounciness };
    case Surface.StickyBlock:
      return { factor: 0.05, nextBounciness: bounciness };
    case Surface.BouncyBlock: {
      if (bounciness <= 0) return { factor: 0.84, nextBounciness: bounciness };
      const nb = bounciness - 0.01;
      const f = speed > 0 ? (nb * 6.5) / speed : 0.84;
      return { factor: f, nextBounciness: nb };
    }
    case Surface.OneWayN:
    case Surface.OneWayE:
    case Surface.OneWayS:
    case Surface.OneWayW:
      return { factor: 0.82, nextBounciness: bounciness };
    case Surface.Breakable4:
    case Surface.Breakable3:
    case Surface.Breakable2:
    case Surface.Breakable1:
      return { factor: 0.9, nextBounciness: bounciness };
    case Surface.MoveableBlock:
    case Surface.MoveableBlock2:
      return { factor: 0.8, nextBounciness: bounciness };
    default:
      return { factor: 1.0, nextBounciness: bounciness };
  }
}

export function isSpeedTile(s: Surface): boolean {
  return s >= Surface.SpeedN && s <= Surface.SpeedNW;
}

/** Per-frame acceleration vector for a speed/arrow tile. */
export function downhillAccel(s: Surface): { ax: number; ay: number } {
  const a = DOWNHILL_ACCEL;
  const d = DOWNHILL_ACCEL * DIAG;
  switch (s) {
    case Surface.SpeedN:
      return { ax: 0, ay: -a };
    case Surface.SpeedNE:
      return { ax: d, ay: -d };
    case Surface.SpeedE:
      return { ax: a, ay: 0 };
    case Surface.SpeedSE:
      return { ax: d, ay: d };
    case Surface.SpeedS:
      return { ax: 0, ay: a };
    case Surface.SpeedSW:
      return { ax: -d, ay: d };
    case Surface.SpeedW:
      return { ax: -a, ay: 0 };
    case Surface.SpeedNW:
      return { ax: -d, ay: -d };
    default:
      return { ax: 0, ay: 0 };
  }
}

export function isTeleportEntry(s: Surface): boolean {
  return s === Surface.TeleBlueIn || s === Surface.TeleRedIn || s === Surface.TeleYellowIn || s === Surface.TeleGreenIn;
}

export function teleportExitFor(s: Surface): Surface | null {
  switch (s) {
    case Surface.TeleBlueIn:
      return Surface.TeleBlueOut;
    case Surface.TeleRedIn:
      return Surface.TeleRedOut;
    case Surface.TeleYellowIn:
      return Surface.TeleYellowOut;
    case Surface.TeleGreenIn:
      return Surface.TeleGreenOut;
    default:
      return null;
  }
}

export function isMine(s: Surface): boolean {
  return s === Surface.Mine || s === Surface.BigMine;
}
